/**
 * Phase 86 Plan 86-05 Task 2 — REVIVE menu.
 *
 * Anchored to a MyTaskRow's REVIVE chip (graveyard zone), mirroring
 * `LogTimePopover.tsx`'s anchoring + Esc + outside-click dismissal shape.
 * Real menu semantics (`role="menu"` / `role="menuitem"`, arrow-key roving
 * focus) rather than a mouse-only div — README "Accessibility": "all action
 * chips must be real `<button>`s... keyboard cursor needs a visible focus
 * ring."
 *
 * Two items — 86-CONTEXT "Task due date — RESOLVED 2026-08-12 (Petra)"
 * locks D-DUE to the milestone-derived `dueAt` and explicitly drops
 * "Change due date" from REVIVE's scope: "there is nothing per-task to
 * edit yet; REVIVE offers Comment and Close as won't do." A menu item that
 * renders without a handler is worse than an absent one — this menu never
 * renders a third, dead item.
 *
 *   - Comment reuses the SAME composer the TOUCH chip opens (one composer,
 *     not two) — `onRequestComment` closes this menu and asks the parent
 *     `MyTaskRow` to open `MtTouchPopover` instead of duplicating it here.
 *   - Close as won't do resolves the task's own project's terminal state
 *     via `useCloseTasks` (Task 2) and surfaces a toast when the project has
 *     none configured — never a silent no-op (T-86-16).
 */
import { useCallback, useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useCloseTasks } from '../../hooks/useCloseTasks';
import { useToast } from '../ui/Toast';

const MENU_ITEM_SELECTOR = '[role="menuitem"]';

export interface MtReviveMenuProps {
  taskId: number;
  taskName: string;
  projectId: number;
  projectName: string;
  open: boolean;
  onClose: () => void;
  /** Closes this menu and opens the shared touch/comment composer instead. */
  onRequestComment: () => void;
}

export function MtReviveMenu({
  taskId,
  taskName,
  projectId,
  projectName,
  open,
  onClose,
  onRequestComment,
}: MtReviveMenuProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const closeTasks = useCloseTasks();
  const toast = useToast();

  // Initial focus → the first menu item, once the panel mounts.
  useEffect(() => {
    if (!open) return;
    const raf = window.requestAnimationFrame(() => {
      firstItemRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [open]);

  // Document-level Escape listener + outside-click close — mirrors
  // LogTimePopover.tsx.
  useEffect(() => {
    if (!open) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }

    function handleMouseDown(e: MouseEvent) {
      const container = containerRef.current;
      if (!container) return;
      if (!container.contains(e.target as Node)) onClose();
    }

    document.addEventListener('keydown', handleKey, true);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open, onClose]);

  // Arrow-key roving focus between menu items — real menu semantics.
  // Enter/Space activation is native `<button>` behaviour; no bespoke
  // key handling needed for that part.
  const handleMenuKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const menu = containerRef.current;
    if (!menu) return;
    const items = Array.from(menu.querySelectorAll<HTMLButtonElement>(MENU_ITEM_SELECTOR));
    if (items.length === 0) return;
    e.preventDefault();
    const currentIndex = items.findIndex((item) => item === document.activeElement);
    let nextIndex: number;
    if (e.key === 'ArrowDown') {
      nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % items.length;
    } else {
      nextIndex = currentIndex === -1 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
    }
    items[nextIndex]?.focus();
  }, []);

  const handleComment = useCallback(() => {
    onRequestComment();
  }, [onRequestComment]);

  const handleClose = useCallback(() => {
    onClose();
    closeTasks.mutate([{ taskId, projectId, projectName }], {
      onSuccess: (result) => {
        const failure = result.failures.find((f) => f.taskId === taskId);
        if (failure) {
          toast(failure.reason, 'error');
        }
      },
      onError: (err) => {
        toast(err instanceof Error ? err.message : `Couldn't close ${taskName}`, 'error');
      },
    });
  }, [closeTasks, onClose, projectId, projectName, taskId, taskName, toast]);

  if (!open) return null;

  return (
    <div
      ref={containerRef}
      role="menu"
      aria-label={`Revive ${taskName}`}
      onKeyDown={handleMenuKeyDown}
      // Belt-and-suspenders alongside MyTaskRow's own open-state guard —
      // see MtTouchPopover.tsx's identical comment.
      onClick={(e) => e.stopPropagation()}
      data-testid="mt-revive-menu"
      className="absolute right-0 top-full mt-1 z-40 w-56 bg-ms-surface border border-ms-border rounded-lg shadow-xl py-1"
    >
      <button
        ref={firstItemRef}
        type="button"
        role="menuitem"
        onClick={handleComment}
        data-testid="mt-revive-menu-comment"
        className="w-full text-left px-3 py-2 text-[11.5px] text-ms-text-strong hover:bg-ms-orange/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ms-orange"
      >
        Comment
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={handleClose}
        data-testid="mt-revive-menu-close"
        className="w-full text-left px-3 py-2 text-[11.5px] text-ms-text-strong hover:bg-ms-orange/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ms-orange"
      >
        Close as won&apos;t do
      </button>
    </div>
  );
}
